<script lang="ts">
	import type { ButtonColor, ButtonSize, ButtonVariant } from '$lib/components/atoms/button/types';
	import type { IconPosition } from '$lib/components/atoms/iconlabel/types';
	import type { IconName } from '$lib/components/icons/types';
	import type { Locale } from '$lib/i18n/types';
	import type { Snippet } from 'svelte';
	import type { ClassValue } from 'svelte/elements';
	import type { RelAttribute, Target } from './types';
	import IconLabel from '$lib/components/atoms/iconlabel/IconLabel.svelte';
	import { i18nPath } from '$lib/i18n/routing';
	import { languageTag } from '$paraglide/runtime';

	interface Props {
		href: string;
		label?: string;
		icon?: IconName;
		iconPosition?: IconPosition;
		button?: ButtonVariant | boolean;
		size?: ButtonSize;
		color?: ButtonColor;
		disabled?: boolean;
		active?: boolean;
		ariaLabel?: string;
		hideTextOnMobile?: boolean;
		itemprop?: string;
		lang?: Locale;
		hreflang?: Locale;
		svelteKitPreloadData?: string;
		rel?: RelAttribute;
		target?: Target;
		class?: ClassValue;
		children?: Snippet;
		onclick?: (event: MouseEvent) => void;
	}

	const {
		href,
		label,
		icon,
		iconPosition = 'left',
		button = false,
		size = 'md',
		color = 'primary',
		disabled = false,
		active = false,
		ariaLabel,
		hideTextOnMobile = false,
		itemprop,
		lang,
		hreflang,
		svelteKitPreloadData,
		rel,
		target,
		class: classes,
		children,
		onclick,
	}: Props = $props();

	const isExternalLinkOrHp = rel === 'external' || href[0] !== '/';
	// Modern browsers seems to automatically treat target="_blank" with rel="noopener", but we add it just in case
	const secureRel = target?.includes('_blank') && !rel?.includes('noopener') ? [rel, 'noopener'].filter(Boolean).join(' ') : rel;
	const buttonClasses = $derived(button ? `btn-${button === true ? 'filled' : button}-${color} btn-${size}` : 'link');
	const i18nHref = $derived(isExternalLinkOrHp ? href : i18nPath(href, languageTag()));
</script>

<!-- eslint-disable svelte/no-navigation-without-resolve -->
<a
	href={i18nHref}
	aria-label={ariaLabel}
	aria-current={active ? 'true' : undefined}
	class={[{
		'min-w-max': !children,
		disabled,
		active,
		'scale-icon-on-hover': icon && !children && !button,
	}, buttonClasses, classes]}
	rel={secureRel}
	{onclick}
	{target}
	{itemprop}
	{lang}
	{hreflang}
	data-sveltekit-preload-data={svelteKitPreloadData}
>
	{#if children}{@render children()}{:else}
		<IconLabel
			{icon}
			{label}
			{iconPosition}
			labelClasses="px-1"
			hideLabelOnMobile={hideTextOnMobile}
		/>
	{/if}
</a>

<style>
	.disabled {
		pointer-events: none;
		cursor: default;
		box-shadow: 0 0 0 #000;
	}

	.link {
		color: currentColor;
		&.active {
			font-weight: bold;
		}
		&.disabled:not(.active) {
			opacity: 0.5;
		}
	}

	.btn {
		text-decoration: none;
		&.disabled:not(.active) {
			filter: grayscale(100);
			opacity: 0.8;
		}
	}

	.scale-icon-on-hover:hover :global([class^='icon-']) {
		transform: scale(1.05);
	}
</style>
